/**
 * Content for the /success page.
 *
 * Two hard rules, both deliberate:
 *   1. No customer, partner or OEM names. Engagements are described by sector
 *      only ("a commercial vehicle manufacturer"), which is non-identifying.
 *   2. No efficiency percentages, timings or project counts.
 *
 * Credibility therefore has to come from specificity. "Detects safe ejection
 * areas and avoids ribs, bosses and cosmetic surfaces" is more persuasive to an
 * engineer than any percentage, because only someone who has actually done the
 * work knows to mention it.
 */

export const differentiators = [
  {
    id: "your-environment",
    title: "Built inside your environment",
    body: "Automation and AI are developed within your working environment and integrated into the CAD and PLM ecosystem you already run. Your product data does not move to somebody else's platform to be useful.",
  },
  {
    id: "no-seat-licence",
    title: "No per-user licence",
    body: "You are not buying seats. Solutions are delivered on a one-time development cost, so the value does not shrink each time you add an engineer to the team.",
  },
  {
    id: "standards",
    title: "We know the standard, not just the software",
    body: "Automating a regulation check means understanding the regulation. Our work spans lighting, head impact, external projections, field of view and direct vision requirements — the engineering intent behind the rule, not only the geometry.",
  },
  {
    id: "breadth",
    title: "Across the whole toolchain",
    body: "Native automation for CATIA, NX, CREO, AutoCAD and Solid Edge, and PLM work across Windchill, Teamcenter, 3DEXPERIENCE and Enovia — including the CAD, ERP and application integrations between them.",
  },
  {
    id: "support",
    title: "Support does not stop at handover",
    body: "Tools meet real models, real edge cases and changing standards. Issue resolution and enhancement are part of the engagement, not a separate contract negotiated after something breaks.",
  },
  {
    id: "engineering-led",
    title: "Engineering-led, not script-led",
    body: "The people writing the automation understand the design problem underneath it. That is why the output respects draft, clearance, bend radius and manufacturability instead of simply producing geometry that satisfies a macro.",
  },
];

export const engagements = [
  {
    id: "regulation-checks",
    sector: "Commercial vehicle manufacturer",
    title: "Regulation checks across a growing variant matrix",
    situation:
      "Lamp position, visibility and non-visibility had to be verified against lighting regulations for every variant in the range. Each new combination multiplied the checking work, and the checks were repeated by hand every time the styling moved.",
    approach:
      "We automated the verification directly in CAD, so position and visibility conditions are evaluated against the regulation for each variant, with the result reported rather than interpreted case by case.",
    result:
      "Compliance checking became a repeatable step in the design loop instead of a bottleneck at the end of it, and stayed consistent as the variant count grew.",
  },
  {
    id: "plastic-trim",
    sector: "Interior systems supplier",
    title: "Design-release checks on plastic trim parts",
    situation:
      "Every trim part release required the same sequence of manual checks — draft, curvature, wall thickness, sink mark risk, locator clearance, weak plastic conditions. Different engineers applied them slightly differently, so quality depended on who did the release.",
    approach:
      "We built the whole checklist as automated analyses with colour-coded output, each one comparing the model against the permitted minimum and reporting where it falls short, with results exported as an editable report.",
    result:
      "The same standard is applied to every part regardless of who releases it, and issues surface while the geometry can still be changed cheaply.",
  },
  {
    id: "mould-tooling",
    sector: "Plastics tooling team",
    title: "Mould design tasks that repeated on every programme",
    situation:
      "Ejector pin layout, entry surface creation and scrap cutter geometry were rebuilt manually for each tool. The work was repetitive, and mistakes in it were expensive because they showed up after the tool had been cut.",
    approach:
      "We automated the geometry-heavy steps: entry surfaces generated from the dieface and trimline, scrap cutter bodies constructed to the correct cutting alignment, and ejector pins distributed across detected safe areas while avoiding ribs, bosses and cosmetic surfaces.",
    result:
      "Tooling engineers spend their time on the decisions that need judgement and less on reconstructing geometry that follows fixed rules.",
  },
  {
    id: "routing",
    sector: "Vehicle packaging team",
    title: "Tube, hose and harness routing in crowded assemblies",
    situation:
      "Routing was manual and had to be redone whenever surrounding components moved. Bend radius limits, clamp spacing, branch management and clash checks all interact, so a late packaging change meant reworking the route from the start.",
    approach:
      "We automated routing from defined start, end and support points, respecting diameter, thickness and bend radius, checking interference against neighbouring components and placing corrugated sections and clip positions correctly.",
    result:
      "Routes can be regenerated when the packaging changes rather than rebuilt, so a late change no longer costs the whole routing effort.",
  },
  {
    id: "drawing-check",
    sector: "Engineering design office",
    title: "Drawing checks that no longer depend on attention span",
    situation:
      "Checking drawings against standards — dimensions, annotations, GD&T, revision and title block correctness — is detailed work where accuracy naturally drops as the set gets longer. Errors that slipped through were found downstream.",
    approach:
      "We built an AI-assisted drawing check that compares a drawing against a master and against the applicable design rules, highlighting differences and non-conformances rather than relying on someone to spot them.",
    result:
      "The last drawing in a long set is checked as carefully as the first, and errors are caught before release rather than after.",
  },
  {
    id: "semiconductor",
    sector: "Semiconductor equipment manufacturer",
    title: "Vacuum hardware built to standard, repeatedly",
    situation:
      "Flange design meant creating bolt hole patterns, counterbores and threads, placing hardware and holding pitch circle diameter and edge distances across a great many holes per assembly. O-ring glands had to hold the right compression ratio — a small dimensional error means a leak, and a leak means downtime.",
    approach:
      "We parameterised both: flange patterns generated as a complete set to the applicable vacuum standards, and O-ring grooves generated against standard gland dimensions rather than calculated by hand each time.",
    result:
      "Standards compliance is built into the geometry rather than checked afterwards, and sealing design stops being a source of rework.",
  },
];

export default engagements;
